import React, { useState } from 'react';
import { Button, Form, Input, Modal } from 'antd';
import { toast } from 'react-toastify';
import agentTokenService, { IGenerateTokenResponse } from '../../../../services/aiChat/agentToken.service';

interface GenerateTokenModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: (result: IGenerateTokenResponse) => void;
}

const GenerateTokenModal: React.FC<GenerateTokenModalProps> = ({ visible, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  const handleCancel = () => {
    form.resetFields();
    setEmailError(null);
    onCancel();
  };

  const onFinish = async (values: { email: string; label: string }) => {
    setLoading(true);
    setEmailError(null);
    try {
      const result = await agentTokenService.generate(values.email.trim(), values.label.trim());
      form.resetFields();
      onSuccess(result);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setEmailError('No Synapse 360 user found with this email address.');
      } else if (err?.response?.status !== 404) {
        // Non-404 errors are already toasted by the request interceptor
        // Only show a fallback if the interceptor didn't handle it
        if (!err?.response) {
          toast.error('Something went wrong. Please try again.');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      wrapClassName="custom-modal"
      title="Generate Agent Token"
      centered
      visible={visible}
      onCancel={handleCancel}
      footer={null}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item
          label="User Email"
          name="email"
          rules={[
            { required: true, message: 'Email is required' },
            { type: 'email', message: 'Enter a valid email address' },
            { max: 255, message: 'Email must be 255 characters or fewer' },
          ]}
          validateStatus={emailError ? 'error' : undefined}
          help={
            emailError ||
            "The Synapse 360 user account this token will authenticate as. The agent will inherit this user's data scope and permissions."
          }
        >
          <Input onChange={() => setEmailError(null)} />
        </Form.Item>

        <Form.Item
          label="Token Label"
          name="label"
          rules={[
            { required: true, message: 'Label is required' },
            { max: 255, message: 'Label must be 255 characters or fewer' },
          ]}
          extra="A name to identify which agent or integration this token belongs to."
        >
          <Input placeholder="e.g. Procurement Agent" />
        </Form.Item>

        <div className="btns-block modal-footer">
          <Button type="primary" htmlType="submit" loading={loading}>
            Generate
          </Button>
          <Button onClick={handleCancel}>Cancel</Button>
        </div>
      </Form>
    </Modal>
  );
};

export default GenerateTokenModal;
